import { LoaderFunction } from 'react-router-dom';
import { AuthContextType } from '../../../../hooks/useAuth';

// GQL
import { client } from '../../../../gql/client';
import { selectAllOrders } from '../../../../gql/selectors/orders';
import { GET_GENERIC_ORDERS } from '../../../../gql/queries/genericOrders';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { FETCH_TEAMS, FETCH_USERS } from '../../../../gql/queries/users';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { store } from '../../../../state/configureStore';
import { loadingData } from '../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { refreshOrderFlags } from '../../../../gql/services/orderFlags';

export const SearchAndCreateHistoricalWorkOrdersLoader: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
  void refreshOrderFlags(userData);
  try {
  const cursorGenericOrders = getCursor(
    tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS
  )(store.getState());
  const dispatch = store.dispatch;

  const roleActive = userData?.user?.user?.rolActive;

  if (userData?.user) {
    // FIX A4: Si concessionId no está seteada, pre-setear si solo hay 1 concesión.
    let effectiveConcessionId = cursorGenericOrders.concessionId;
    if (!effectiveConcessionId) {
      try {
        const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
        if (concessions?.length === 1) {
          effectiveConcessionId = concessions[0].id;
          dispatch(setCursor(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, { ...cursorGenericOrders, concessionId: effectiveConcessionId }));
        }
      } catch { /* continue without concessionId */ }
    }

    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido.
    dispatch(loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, false));

    const readGenericOrders = client.readQuery({
      query: GET_GENERIC_ORDERS,
      variables: {
        input: {
          take: cursorGenericOrders.take,
          cursor: cursorGenericOrders.cursor,
          concessionId: effectiveConcessionId,
          teamIds: cursorGenericOrders.teamIds ?? [],
          capitolId: cursorGenericOrders.capitolId,
          subCapitolId: cursorGenericOrders.subCapitolId,
          sortField: cursorGenericOrders.sortField,
          orderBy: cursorGenericOrders.orderBy,
          toDate: cursorGenericOrders.toDate,
          fromDate: cursorGenericOrders.fromDate,
          indexType: cursorGenericOrders.indexType,
          indexExpedition: cursorGenericOrders.indexExpedition,
          classification: cursorGenericOrders.classification,
          predefinedOrderId: cursorGenericOrders.predefinedOrderId,
          states: cursorGenericOrders.states,
          userIds: cursorGenericOrders.userIds,
          pkInitKm: cursorGenericOrders.pkInitKm,
          pkInitMeter: cursorGenericOrders.pkInitMeter,
          pkEndKm: cursorGenericOrders.pkEndKm,
          pkEndMeter: cursorGenericOrders.pkEndMeter,
          description: cursorGenericOrders.description,
          track: cursorGenericOrders.track,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: cursorGenericOrders.onlyFlagged,
        },
      },
    });

    const pageInfo = readGenericOrders?.genericOrders?.pageInfo;

    const totalCount = readGenericOrders?.genericOrders?.totalCount;
    const isCacheEmpty = JSON.stringify(readGenericOrders) === '{}';

    const genericOrders =
      readGenericOrders &&
      !isCacheEmpty &&
      (await selectAllOrders(readGenericOrders?.genericOrders?.genericOrders));

    const [
      {
        data: { capitols },
      },
      {
        data: { concessions },
      },
      {
        data: { subCapitols },
      },
      {
        data: { classifications },
      },
      {
        data: { teams },
      },
      {
        data: { predefinedOrders },
      },
      {
        data: { users },
      },
    ] = await Promise.all([
      client.query({
        query: FETCH_CAPITOLS,
      }),
      client.query({
        query: GET_CONCESSIONS,
      }),
      client.query({
        query: FETCH_SUB_CAPITOLS,
      }),
      client.query({
        query: FETCH_CLASSIFICATIONS,
      }),
      client.query({
        query: FETCH_TEAMS,
      }),
      client.query({
        query: FETCH_PREDEFINED_ORDERS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_USERS,
        fetchPolicy: 'cache-first',
      }),
    ]);

    const filtersDataOptions = {
      capitols,
      concessions,
      subCapitols,
      classifications,
      teams,
      predefinedOrders,
      users,
    };

    const combinedData = {
      genericOrders,
      filtersDataOptions,
      totalCount,
      pageInfo,
      roleActive,
    };

    if (!readGenericOrders || isCacheEmpty) {
      dispatch(
        loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, true)
      );
      try {
      let data;
      if (isCacheEmpty) {
        // Tarea #11: respetar el take guardado en cursor (page-size selector).
        const effectiveTake = firstPageTake(cursorGenericOrders.take, takeBase);
        data = await client.query({
          query: GET_GENERIC_ORDERS,
          variables: {
            input: {
              take: effectiveTake,
              cursor: '',
              concessionId: effectiveConcessionId,
              teamIds: cursorGenericOrders.teamIds ?? [],
              capitolId: cursorGenericOrders.capitolId,
              subCapitolId: cursorGenericOrders.subCapitolId,
              sortField: cursorGenericOrders.sortField,
              orderBy: cursorGenericOrders.orderBy ?? 'desc',
              toDate: cursorGenericOrders.toDate,
              fromDate: cursorGenericOrders.fromDate,
              indexType: cursorGenericOrders.indexType,
              indexExpedition: cursorGenericOrders.indexExpedition,
              classification: cursorGenericOrders.classification,
              predefinedOrderId: cursorGenericOrders.predefinedOrderId,
              states: cursorGenericOrders.states,
              userIds: cursorGenericOrders.userIds,
              pkInitKm: cursorGenericOrders.pkInitKm,
              pkInitMeter: cursorGenericOrders.pkInitMeter,
              pkEndKm: cursorGenericOrders.pkEndKm,
              pkEndMeter: cursorGenericOrders.pkEndMeter,
              description: cursorGenericOrders.description,
              track: cursorGenericOrders.track,
              onlyFlagged: cursorGenericOrders.onlyFlagged,
            },
          },
        });
        const setCursorData = {
          take: effectiveTake,
          cursor: '',
          concessionId: effectiveConcessionId,
          teamIds: cursorGenericOrders.teamIds ?? [],
          capitolId: cursorGenericOrders.capitolId,
          subCapitolId: cursorGenericOrders.subCapitolId,
          sortField: cursorGenericOrders.sortField,
          orderBy: cursorGenericOrders.orderBy ?? 'desc',
          toDate: cursorGenericOrders.toDate,
          fromDate: cursorGenericOrders.fromDate,
          indexType: cursorGenericOrders.indexType,
          indexExpedition: cursorGenericOrders.indexExpedition,
          classification: cursorGenericOrders.classification,
          predefinedOrderId: cursorGenericOrders.predefinedOrderId,
          states: cursorGenericOrders.states,
          userIds: cursorGenericOrders.userIds,
          pkInitKm: cursorGenericOrders.pkInitKm,
          pkInitMeter: cursorGenericOrders.pkInitMeter,
          pkEndKm: cursorGenericOrders.pkEndKm,
          pkEndMeter: cursorGenericOrders.pkEndMeter,
          description: cursorGenericOrders.description,
          track: cursorGenericOrders.track,
          onlyFlagged: cursorGenericOrders.onlyFlagged,
        };

        dispatch(
          setCursor(
            tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS,
            setCursorData
          )
        );
      } else {
        data = await client.query({
          query: GET_GENERIC_ORDERS,
          variables: {
            input: {
              take: cursorGenericOrders.take,
              cursor: cursorGenericOrders.cursor,
              concessionId: effectiveConcessionId,
              teamIds: cursorGenericOrders.teamIds ?? [],
              capitolId: cursorGenericOrders.capitolId,
              subCapitolId: cursorGenericOrders.subCapitolId,
              sortField: cursorGenericOrders.sortField,
              orderBy: cursorGenericOrders.orderBy,
              toDate: cursorGenericOrders.toDate,
              fromDate: cursorGenericOrders.fromDate,
              indexType: cursorGenericOrders.indexType,
              indexExpedition: cursorGenericOrders.indexExpedition,
              classification: cursorGenericOrders.classification,
              predefinedOrderId: cursorGenericOrders.predefinedOrderId,
              states: cursorGenericOrders.states,
              userIds: cursorGenericOrders.userIds,
              pkInitKm: cursorGenericOrders.pkInitKm,
              pkInitMeter: cursorGenericOrders.pkInitMeter,
              pkEndKm: cursorGenericOrders.pkEndKm,
              pkEndMeter: cursorGenericOrders.pkEndMeter,
              description: cursorGenericOrders.description,
              track: cursorGenericOrders.track,
              onlyFlagged: cursorGenericOrders.onlyFlagged,
            },
          },
        });
      }

      const loading = data.loading;
      const pageInfo = data.data.genericOrders.pageInfo;

      const totalCount = data.data.genericOrders.totalCount;

      const genericOrders = await selectAllOrders(
        data.data.genericOrders.genericOrders
      );

      const [
        {
          data: { capitols },
        },
        {
          data: { concessions },
        },
        {
          data: { subCapitols },
        },
        {
          data: { classifications },
        },
        {
          data: { teams },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { users },
        },
      ] = await Promise.all([
        client.query({
          query: FETCH_CAPITOLS,
        }),
        client.query({
          query: GET_CONCESSIONS,
        }),
        client.query({
          query: FETCH_SUB_CAPITOLS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
        client.query({
          query: FETCH_TEAMS,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
          fetchPolicy: 'cache-first',
        }),
        client.query({
          query: FETCH_USERS,
          fetchPolicy: 'cache-first',
        }),
      ]);

      const filtersDataOptions = {
        capitols,
        concessions,
        subCapitols,
        classifications,
        teams,
        predefinedOrders,
        users,
      };

      const combinedData = {
        genericOrders,
        filtersDataOptions,
        totalCount,
        pageInfo,
        loading,
        roleActive,
      };
      return combinedData;
      } finally {
        // FIX (loader-loading-flag-leak): cierra el flag aunque MISS lance.
        dispatch(
          loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, false)
        );
      }
    }

    return combinedData;
  }
  return {};
  } catch (err) {
    console.log('Loader offline fallback:', err);
    // FIX (loader-loading-flag-leak): defensa para errores antes del MISS branch.
    store.dispatch(
      loadingData(tableIds.SEARCH_AND_CREATE_HISTORICAL_WORKORDERS, false)
    );
    return { genericOrders: [], filtersDataOptions: { capitols: [], concessions: [], subCapitols: [], classifications: [], teams: [], predefinedOrders: [] }, totalCount: 0, pageInfo: { hasNextPage: false, hasPreviousPage: false }, roleActive: userData?.user?.user?.rolActive };
  }
};

  return { data: load() };
};
